import React from 'react';
import BundleDisclaimer from '../BundleDisclaimer';
import { formatEUR } from '../../hooks/usePrices';

/**
 * Right-hand sticky summary card.
 *
 * Lists every cart item with its specs + price + remove button, then the
 * current draft (if the user is still on steps 1-3), then the live grand
 * total and the bundle disclaimer footer.
 *
 * Pure presentation — all state + handlers come from the orchestrator.
 */
export default function SummaryAside({
  t,
  step,
  cart,
  draft,         // { productName, sizeValid, widthNum, heightNum, color, mesh }
  grandTotal,
  estPrice,
  bundleActive,  // boolean — toggles the whiteStandard note in disclaimer
  disclaimerLabels,
  onRemoveItem,
}) {
  const { productName, sizeValid, widthNum, heightNum, color, mesh } = draft;

  return (
    <aside className="lg:col-span-4 lg:sticky lg:top-28 h-fit" data-testid="cfg-summary">
      <div className="rounded-2xl bg-[#1F1D1A] text-white p-6 lg:p-7 shadow-[0_30px_60px_-30px_rgba(31,29,26,0.45)]">
        <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#F26B1A] mb-3">{t.summary}</p>

        {cart.length > 0 && (
          <ul className="space-y-3 mb-5 pb-5 border-b border-white/15">
            {cart.map((it, i) => (
              <li key={it.uid || `summary-${i}`} className="text-[12.5px]">
                <div className="flex items-start justify-between gap-3 mb-1.5">
                  <p className="text-white/55 text-[10px] uppercase tracking-[0.16em] font-semibold">
                    {t.itemNumFmt.replace('{n}', String(i + 1))}
                  </p>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-bold tabular-nums whitespace-nowrap text-[13px]">{formatEUR(it.estPrice)}</span>
                    <button
                      type="button"
                      onClick={() => onRemoveItem(i)}
                      data-testid={`cfg-summary-remove-${i}`}
                      aria-label={t.remove}
                      className="inline-flex w-5 h-5 rounded-full bg-white/10 hover:bg-[#F26B1A] text-white/70 hover:text-white items-center justify-center transition-colors"
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M6 6l12 12M6 18L18 6" />
                      </svg>
                    </button>
                  </div>
                </div>
                <dl className="space-y-1 text-[11.5px]">
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-white/55">{t.steps[0]}</dt>
                    <dd className="font-semibold text-right truncate ml-2">{it.productName}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-white/55">{t.steps[1]}</dt>
                    <dd className="font-semibold tabular-nums">{it.width} × {it.height} {t.cm}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-white/55">{t.color}</dt>
                    <dd className="font-semibold">{it.colorLabel}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-white/55">{t.mesh}</dt>
                    <dd className="font-semibold text-right truncate ml-2">{it.meshLabel}</dd>
                  </div>
                  {it.openingDir && (
                    <div className="flex items-baseline justify-between gap-3">
                      <dt className="text-white/55">{t.openingDir}</dt>
                      <dd className="font-semibold">{it.openingDir === 'L' ? t.dirLeft : t.dirRight}</dd>
                    </div>
                  )}
                  {it.slideDir && (
                    <div className="flex items-baseline justify-between gap-3">
                      <dt className="text-white/55">{t.slideDir}</dt>
                      <dd className="font-semibold">{it.slideDir === 'L' ? t.dirLeft : t.dirRight}</dd>
                    </div>
                  )}
                  {it.crossbarMm && (
                    <div className="flex items-baseline justify-between gap-3">
                      <dt className="text-white/55">{t.crossbar}</dt>
                      <dd className="font-semibold tabular-nums">{it.crossbarMm} mm</dd>
                    </div>
                  )}
                </dl>
              </li>
            ))}
          </ul>
        )}

        {step < 4 && (
          <>
            <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-white/55 mb-2">
              {cart.length > 0
                ? t.itemNumFmt.replace('{n}', String(cart.length + 1))
                : t.itemNumFmt.replace('{n}', '1')}
            </p>
            <ul className="space-y-2.5 text-[13.5px]">
              <li className="flex items-baseline justify-between gap-3 border-b border-white/10 pb-2">
                <span className="text-white/70">{t.steps[0]}</span>
                <span className={`font-semibold text-right truncate ml-2 ${productName ? '' : 'text-white/30'}`}>{productName || '—'}</span>
              </li>
              <li className="flex items-baseline justify-between gap-3 border-b border-white/10 pb-2">
                <span className="text-white/70">{t.steps[1]}</span>
                <span className={`font-semibold tabular-nums ${sizeValid ? '' : 'text-white/30'}`}>
                  {sizeValid ? `${widthNum} × ${heightNum} ${t.cm}` : '—'}
                </span>
              </li>
              <li className="flex items-baseline justify-between gap-3 border-b border-white/10 pb-2">
                <span className="text-white/70">{t.color}</span>
                <span className={`font-semibold ${color ? '' : 'text-white/30'}`}>{color ? t.colorNames[color.id] : '—'}</span>
              </li>
              <li className="flex items-baseline justify-between gap-3 pb-2">
                <span className="text-white/70">{t.mesh}</span>
                <span className={`font-semibold ${mesh ? '' : 'text-white/30'}`}>{mesh ? t.meshNames[mesh.id] : '—'}</span>
              </li>
            </ul>
          </>
        )}

        <div className="mt-6 pt-5 border-t border-white/15">
          <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-white/70 mb-1">
            {cart.length > 0 ? t.totalLabel : t.estPrice}
          </p>
          <p className="font-display text-[40px] leading-none tabular-nums" data-testid="cfg-price">
            {step < 4
              ? (formatEUR((grandTotal || 0) + (estPrice || 0)) || (grandTotal > 0 ? formatEUR(grandTotal) : '—'))
              : (grandTotal > 0 ? formatEUR(grandTotal) : '—')}
          </p>
          <p className="text-[11.5px] text-white/60 mt-2">{t.estNote}</p>
        </div>

        <BundleDisclaimer
          data-testid="cfg-summary-disclaimer"
          labels={disclaimerLabels}
          variant="inline"
          showWhiteStandard={bundleActive}
          className="mt-4 pt-4 border-t border-white/15 text-white/75"
        />
      </div>
    </aside>
  );
}
